import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/features/billing/billing-format";
import type { PlanInterval, SubscriptionProduct } from "@/types";

interface SubscriptionPlanCardProps {
  interval: PlanInterval;
  loading: string | null;
  onSubscribe: (productId: string, priceId: string, planName?: string) => void;
  plan: SubscriptionProduct;
}

export function SubscriptionPlanCard({
  interval,
  loading,
  onSubscribe,
  plan,
}: SubscriptionPlanCardProps) {
  const { t } = useTranslation(["billing"]);
  const price = plan.prices.find((p) => p.interval === interval);
  if (!price) return null;

  return (
    <div
      className={`rounded-lg border p-4 ${
        plan.highlighted
          ? "border-info ring-2 ring-info/30"
          : "border-border"
      }`}
    >
      {plan.badge && (
        <span className="inline-block rounded-full bg-[color:color-mix(in_srgb,var(--info)_16%,var(--card))] px-2 py-0.5 text-xs font-medium text-info mb-2">
          {plan.badge}
        </span>
      )}
      <h4 className="text-lg font-semibold text-foreground">{plan.name}</h4>
      <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>

      <div className="mt-4">
        <span className="text-2xl font-bold text-foreground">
          {formatCurrency(price.amount, plan.currency.toUpperCase())}
        </span>
        <span className="text-sm text-muted-foreground">/{interval}</span>
      </div>

      <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
        {plan.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2">
            <svg
              className="h-4 w-4 text-success mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <Button
        variant={plan.highlighted ? "default" : "secondary"}
        onClick={() => onSubscribe(plan.id, price.id, plan.name)}
        disabled={loading === price.id}
        className="mt-4 w-full"
        data-test="billing-subscribe"
      >
        {loading === price.id
          ? t("billing:plans.loading")
          : t("billing:plans.subscribe")}
      </Button>
    </div>
  );
}
